// 回收站视图
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { FileItem } from '../lib/db';
import { formatBytes } from '../lib/utils';
import { makeT, type Lang } from '../lib/i18n';
import { useStore } from '../lib/store';
import { ConfirmModal } from '../components/ConfirmModal';

export function TrashView({ lang, onBack }: { lang: Lang; onBack: () => void }) {
  const [items, setItems] = useState<FileItem[]>([]);
  const [confirm, setConfirm] = useState<{ mode: 'perma' | 'clear'; items: FileItem[] } | null>(null);
  const [toast, setToast] = useState('');
  const theme = useStore((s) => s.theme);
  const toggleTheme = useStore((s) => s.toggleTheme);
  const setLang = useStore((s) => s.setLang);
  const t = makeT(lang);

  const load = async () => {
    const r = await api.get('/api/files?include=trash');
    setItems(r.items);
  };
  useEffect(() => { load(); }, []);

  const showToast = (m: string) => { setToast(m); setTimeout(() => setToast(''), 2500); };
  const trashKeyOf = (it: FileItem) => it.uuid;

  const restore = async (it: FileItem) => {
    await api.post('/api/files/restore/' + encodeURIComponent(trashKeyOf(it)));
    showToast(t('ops.restore'));
    await load();
  };
  const perma = async (it: FileItem) => {
    await api.del('/api/files?hard=true', { keys: [trashKeyOf(it)] });
    showToast('✓');
    await load();
  };
  const clearTrash = async () => {
    const r = await api.post('/api/trash/clear');
    showToast((r.cleared || 0) + (lang === 'zh' ? ' 项已清空' : ' cleared'));
    await load();
  };

  return (
    <div className="app">
      {/* 完整顶部栏 */}
      <header className="header">
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="icon-btn" onClick={onBack} title={t('header.back')}>←</button>
          <h1 style={{ fontSize: 18, margin: 0, fontWeight: 700 }}>🗑 {t('header.trash')}</h1>
        </div>
        <div className="actions">
          {items.length > 0 && (
            <button className="icon-btn" onClick={() => setConfirm({ mode: 'clear', items: [] })} title={lang === 'zh' ? '清空回收站' : 'Empty trash'}>🗑</button>
          )}
          <button className="icon-btn" onClick={() => setLang(lang === 'zh' ? 'en' : 'zh')} title="Lang">{lang === 'zh' ? 'EN' : '中'}</button>
          <button className="icon-btn" onClick={toggleTheme} title="Theme">{theme === 'dark' ? '☀️' : '🌙'}</button>
        </div>
      </header>

      <main className="main">
        <div className="trash">
          {items.length === 0 ? (
            <div className="empty"><div className="emoji">🗑</div><p>{lang === 'zh' ? '回收站是空的' : 'Trash is empty'}</p></div>
          ) : (
            <div>
              {items.map((it) => (
                <div className="trash-item" key={trashKeyOf(it)}>
                  <div className="nm">{it.displayName}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 12 }}>{formatBytes(it.size)}</div>
                  <div className="btns">
                    <button className="restore" onClick={() => restore(it)}>{t('ops.restore')}</button>
                    <button className="del" onClick={() => setConfirm({ mode: 'perma', items: [it] })}>{t('ops.permanent')}</button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {confirm && (
        <ConfirmModal
          lang={lang}
          title={confirm.mode === 'clear' ? (lang === 'zh' ? '清空回收站?' : 'Empty trash?') : t('confirm.perma', { n: confirm.items.length })}
          body={confirm.mode === 'clear' ? (lang === 'zh' ? '将永久删除回收站所有文件,不可恢复。' : 'This will permanently delete all items in trash.') : t('confirm.deleteBody')}
          items={confirm.items.map((i) => i.displayName)}
          confirmLabel={t('ops.permanent')}
          cancelLabel={t('confirm.cancel')}
          onConfirm={() => {
            if (confirm.mode === 'clear') clearTrash();
            else Promise.all(confirm.items.map(perma));
            setConfirm(null);
          }}
          onCancel={() => setConfirm(null)}
        />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}